//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useState } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import NotFound from './components/NotFound/NotFound';
import Home from './components/Home/Home';
import { useConfig } from './Config';
import SwitcherIframe from './components/switcher/SwitcherIframe';
import EmbedSwitcher from './components/switcher/EmbedSwitcher';
import Logout from './components/Logout/Logout';
import LoggedOut from './components/Logout/LoggedOut';
import ProtectedRoute from './ProtectedRoute';
import Login from './components/Login/Login';
import PluginPage from './components/PluginPage/Plugin';
import Loops from './components/Loops/LoopHome';
import Landing from './components/Create Loop/Landing';
import LoopDetailsPage from './components/Loops/LoopDetails';
import UserAdminLayout from './components/UserAdmin/UserAdminLayout';
import Integration from './components/Integration/Integration';
import Announcements from './components/Announcements/Announcements';
import SupportPage from './components/Support/SupportPage';
import GroupsPage from './components/UserGroups/GroupsPage';
import MemberAccessPage from './components/UserGroups/MemberAccessPage';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import '@fontsource/inter';
import '@fontsource/roboto';
import 'primeicons/primeicons.css';
import LoginError from './components/Error/SolutionError';
import OpenSearchDashboardPage from './components/Dashboards/OpenSearchDashboardPage';
import SearchResults from './components/search/SearchResults';
import Documentation from './components/Documentation/Documentation';
import WikiPage from './components/Wiki/WikiPage';
import { useLoopContext } from './LoopContext';

const ContextTestApp =
  process.env.NODE_ENV === 'development'
    ? React.lazy(() => import('./components/ContextTest/ContextTestApp'))
    : () => null;

function App() {
  const { solutions } = useConfig();
  const { loopLinksResponseInContext } = useLoopContext();

  // Use state to track the latest loopLinksResponse
  const [loopLinksResponse, setLoopLinksResponseState] = useState(
    loopLinksResponseInContext ?? {},
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const latest = loopLinksResponseInContext ?? {};
      // Compare shallowly, update state if changed
      if (
        Object.keys(latest).length !== Object.keys(loopLinksResponse).length ||
        Object.entries(latest).some(([k, v]) => loopLinksResponse[k] !== v)
      ) {
        setLoopLinksResponseState(latest);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [loopLinksResponse]);

  const getLoopLinks = (
    loopLinksResponse: Record<string, string | undefined>,
  ) => {
    const loopNameMap: Record<string, string> = {
      planLoop: 'Plan',
      measureLoop: 'Measure',
      deployLoop: 'Deploy',
      testLoop: 'Test',
      releaseLoop: 'Release',
      controlLoop: 'Control',
      buildLoop: 'Build',
    };

    return Object.entries(loopLinksResponse)
      .filter(([key, url]) => loopNameMap[key] && typeof url === 'string')
      .map(([key, url]) => ({
        name: loopNameMap[key],
        url: url as string,
      }));
  };
  const loopLinks = getLoopLinks(loopLinksResponse);

  const getRoutePath = (
    solutions: { name: string; path: string; routePath: string }[],
    name: string,
  ): string | undefined => {
    const solution = solutions.find((app) => app.name === name);
    if (solution) {
      return solution.routePath.endsWith('/')
        ? solution.routePath.slice(0, -1)
        : solution.routePath;
    }
    return undefined;
  };

  type Solution = { name: string; path: string; routePath: string };

  const loopRoutes = [
    {
      path: '/link/plan',
      name: 'Plan',
    },
    {
      path: '/link/measure',
      name: 'Measure',
    },
    {
      path: '/link/deploy',
      name: 'Deploy',
    },
    {
      path: '/link/test',
      name: 'Test',
    },
    {
      path: '/link/release',
      name: 'Release',
    },
    {
      path: '/link/control',
      name: 'Control',
    },
    {
      path: '/link/build',
      name: 'Build',
    },
  ];

  const modernizedRoutes = [
    {
      path: getRoutePath(solutions as Solution[], 'Test') ?? '',
      name: 'Test',
    },
    {
      path: getRoutePath(solutions as Solution[], 'Build') ?? '',
      name: 'Build',
    },
    {
      path: getRoutePath(solutions as Solution[], 'Code') ?? '',
      name: 'Code',
    },
    {
      path: getRoutePath(solutions as Solution[], 'Control') ?? '',
      name: 'Control',
    },
    {
      path: getRoutePath(solutions as Solution[], 'Measure') ?? '',
      name: 'Measure',
    },
    {
      path: getRoutePath(solutions as Solution[], 'Release') ?? '',
      name: 'Release',
    },
    {
      path: getRoutePath(solutions as Solution[], 'Plan') ?? '',
      name: 'Plan',
    },
    {
      path: getRoutePath(solutions as Solution[], 'Deploy') ?? '',
      name: 'Deploy',
    },
  ];

  const legacyRoutes = [
    {
      path: getRoutePath(solutions as Solution[], 'Model') ?? '',
      name: 'Model',
    },
  ];

  const getIframeSrc = (appName: string): string | undefined => {
    if (Array.isArray(solutions)) {
      const app = solutions.find((solution) => solution.name === appName);
      return app ? app.path : undefined;
    }
    return undefined;
  };

  const renderRoutes = () => (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/logout" element={<Logout />} />
      <Route path="/logged-out" element={<LoggedOut />} />
      <Route path="/" element={<ProtectedRoute element={<Loops />} />} />

      <Route
        path="/solutions"
        element={<ProtectedRoute element={<Home />} />}
      />

      <Route
        path="/documentation"
        element={<ProtectedRoute element={<Documentation />} />}
      />

      <Route
        path="/getStarted"
        element={<ProtectedRoute element={<Home />} />}
      />

      <Route
        path="/embed"
        element={<ProtectedRoute element={<EmbedSwitcher />} />}
      />
      <Route
        path="/plugin"
        element={<ProtectedRoute element={<PluginPage />} />}
      />
      <Route
        path="/user-administration"
        element={<ProtectedRoute requiresAdmin element={<UserAdminLayout />} />}
      />
      <Route
        path="/integration"
        element={<ProtectedRoute requiresAdmin element={<Integration />} />}
      />
      <Route
        path="/announcements"
        element={<ProtectedRoute requiresAdmin element={<Announcements />} />}
      />
      <Route
        path="/user-groups"
        element={<ProtectedRoute requiresAdmin element={<GroupsPage />} />}
      />

      {/* Member access is the end-user view of their own integration access, so unlike
          /user-groups it is deliberately NOT admin-gated. */}
      <Route
        path="/member-access"
        element={<ProtectedRoute element={<MemberAccessPage />} />}
      />

      <Route
        path="/error/login"
        element={<ProtectedRoute element={<LoginError />} />}
      />

      <Route
        path="/dashboard"
        element={<ProtectedRoute element={<OpenSearchDashboardPage />} />}
      />

      {modernizedRoutes.map(({ path, name }) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute
              element={
                getIframeSrc(name) ? (
                  <SwitcherIframe
                    iframeSrc={getIframeSrc(name) ?? ''}
                    appName={name}
                    path={path}
                    switcherPosition="right"
                  />
                ) : (
                  <Navigate to="*" />
                )
              }
            />
          }
        />
      ))}

      {legacyRoutes.map(({ path, name }) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute
              element={
                getIframeSrc(name) ? (
                  <SwitcherIframe
                    iframeSrc={getIframeSrc(name) ?? ''}
                    appName={name}
                    path={path}
                    switcherPosition="left"
                  />
                ) : (
                  <Navigate to="*" />
                )
              }
            />
          }
        />
      ))}

      {loopRoutes.map(({ path, name }) => (
        <Route
          key={path}
          path={path}
          element={<ProtectedRoute element={<EmbedSwitcher />} />}
        />
      ))}

      <Route
        path="/loop"
        element={
          <ProtectedRoute requiresAdmin element={<Landing mode={'loop'} />} />
        }
      />
      <Route
        path="/teamspace"
        element={
          <ProtectedRoute
            requiresAdmin
            element={<Landing mode={'teamspace'} />}
          />
        }
      />

      <Route
        path="/loop-details"
        element={<ProtectedRoute element={<LoopDetailsPage />} />}
      />
      <Route
        path="/search"
        element={<ProtectedRoute element={<SearchResults />} />}
      />

      <Route
        path="/support"
        element={<ProtectedRoute requiresAdmin element={<SupportPage />} />}
      />

      {process.env.NODE_ENV === 'development' && (
        <Route
          path="/context-test"
          element={
            <React.Suspense fallback={null}>
              <ProtectedRoute element={<ContextTestApp />} />
            </React.Suspense>
          }
        />
      )}

      {/* Add more new routes above this point */}
      <Route path="/wiki" element={<ProtectedRoute element={<WikiPage />} />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );

  return (
    <div id="id-app-root" data-testid="app-root">
      {renderRoutes()}
    </div>
  );
}

export default App;
